import React, { useEffect, useMemo, useState } from "react";
import {
  BookOpen, Workflow, AlertTriangle, FileText, Search, Lightbulb, PlusCircle,
  Plus, ChevronRight, Edit3, Trash2, Loader2, Building2, Users, UserRound,
  FileSignature, Stethoscope, Files, IdCard, ClipboardList, TestTube,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  BUSINESS_GUIDES, UNIVERSAL_WORKFLOW, EXCEPTION_WORKFLOWS, COMPANY_SOPS,
} from "@/components/help/helpContent";
import {
  QuickLeadDialog, QuickFacilityDialog, QuickContractDialog,
  QuickInvoiceDialog, QuickPaymentDialog, QuickRefundDialog,
  QuickDocumentDialog, QuickStaffCredentialDialog,
  QuickTaskDialog, QuickInternalNoteDialog,
} from "@/components/help/QuickActionDialogs";
import SpecimenTrainingPanel from "@/components/help/SpecimenTrainingPanel";
import {
  listInternalNotes, createInternalNote, updateInternalNote, deleteInternalNote,
  INTERNAL_NOTE_CATEGORIES,
} from "@/lib/api";

const SECTIONS = [
  { id: "guides", label: "Setup Guides", icon: BookOpen },
  { id: "workflow", label: "Universal Workflow", icon: Workflow },
  { id: "exceptions", label: "Exception Flows", icon: AlertTriangle },
  { id: "sops", label: "Company SOPs", icon: FileText },
  { id: "specimen", label: "Specimen & Training", icon: TestTube },
  { id: "kb", label: "Knowledge Base", icon: Search },
  { id: "notes", label: "Internal Notes", icon: Lightbulb },
  { id: "entry", label: "Quick Actions", icon: PlusCircle },
];

const CAT_LABEL = Object.fromEntries(INTERNAL_NOTE_CATEGORIES.map((c) => [c.value, c.label]));
const CAT_COLORS = {
  business_ideas: { bg: "rgba(212,175,55,0.18)", color: "#7A5E12" },
  expansion_plans: { bg: "rgba(11,37,69,0.10)", color: "#0B2545" },
  future_services: { bg: "rgba(15,109,93,0.12)", color: "#0F6D5D" },
  partnerships: { bg: "rgba(212,175,55,0.18)", color: "#7A5E12" },
  vendor_notes: { bg: "#F1F5F9", color: "#475569" },
  strategic_goals: { bg: "rgba(155,28,28,0.10)", color: "#9B1C1C" },
};

const MANUAL_ICONS = {
  Users, UserRound, Building2, FileSignature, Stethoscope, Files, IdCard, ClipboardList,
};

// Quick Actions — 12 dialogs that create records inline without leaving the Help Center.
// Each card lists: (id, title, desc, icon, dialog kind, label of resulting "saved to")
const QUICK_ACTIONS = [
  { id: "lead", title: "Add Lead", desc: "Capture an inbound opportunity (phone, email, referral).", icon: "Users", kind: "lead" },
  { id: "client", title: "Add Client", desc: "Onboard a recurring/active customer — same workflow as a lead.", icon: "UserRound", kind: "client" },
  { id: "facility", title: "Add Facility / Provider", desc: "B2B facility or healthcare provider partner.", icon: "Building2", kind: "facility" },
  { id: "contract", title: "Add Contract", desc: "Quick contract record, optional signed PDF.", icon: "FileSignature", kind: "contract" },
  { id: "service_request", title: "Add Service Request", desc: "Service request on behalf of a customer.", icon: "Stethoscope", kind: "service_request" },
  { id: "invoice", title: "Add Invoice", desc: "Record amount due + Stripe link on an existing lead.", icon: "FileText", kind: "invoice" },
  { id: "payment", title: "Record Payment", desc: "Mark a payment received on a lead.", icon: "FileText", kind: "payment" },
  { id: "refund", title: "Create Refund Request", desc: "Flag a lead as refund pending, with reason on file.", icon: "AlertTriangle", kind: "refund" },
  { id: "document", title: "Upload Document", desc: "Client, business, or financial record.", icon: "Files", kind: "document" },
  { id: "staff", title: "Add Staff Credential", desc: "CPR/BLS, license, certification, or any staff doc.", icon: "IdCard", kind: "staff" },
  { id: "task", title: "Create Task", desc: "Personal reminder or shared to-do.", icon: "ClipboardList", kind: "task" },
  { id: "note", title: "Add Note", desc: "Idea, plan, vendor note — indexed by Knowledge Base.", icon: "Lightbulb", kind: "note" },
];

const QUICK_ICON_MAP = {
  Users, UserRound, Building2, FileSignature, Stethoscope, Files, IdCard, ClipboardList,
  Lightbulb, FileText, AlertTriangle, PlusCircle,
};

const Pill = ({ children, color = "#475569", bg = "#F1F5F9" }) => (
  <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
        style={{ background: bg, color }}>{children}</span>
);

// =====================================================================
// Setup Guides
// =====================================================================
const GuidesSection = () => (
  <div className="space-y-3" data-testid="help-guides">
    <SectionIntro
      title="Business Setup Guides"
      subtitle="Quick how-tos for every common task in T&J Clinical Solutions."
      icon={BookOpen}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {BUSINESS_GUIDES.map((g) => (
        <details key={g.id} className="group rounded-xl border bg-white p-4 transition-shadow open:shadow-sm"
                 style={{ borderColor: "#E2E8F0" }} data-testid={`guide-${g.id}`}>
          <summary className="flex cursor-pointer items-start gap-3 list-none">
            <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                  style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
              <BookOpen className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold leading-tight" style={{ color: "#0B2545" }}>
                {g.title}
              </div>
              <p className="text-xs mt-0.5 line-clamp-1" style={{ color: "#64748B" }}>{g.where}</p>
            </div>
            <ChevronRight className="h-4 w-4 mt-1 shrink-0 transition-transform group-open:rotate-90"
                          style={{ color: "#94A3B8" }} />
          </summary>
          <div className="mt-3 ml-11 space-y-2">
            <p className="text-xs italic" style={{ color: "#475569" }}>{g.where}</p>
            <ol className="text-xs space-y-1.5 list-none" style={{ color: "#0B2545" }}>
              {g.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="grid place-items-center h-4 w-4 rounded-full text-[9px] font-bold mt-0.5 shrink-0"
                        style={{ background: "#0B2545", color: "#FFD774" }}>{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </details>
      ))}
    </div>
  </div>
);

// =====================================================================
// Universal Workflow
// =====================================================================
const WorkflowSection = () => (
  <div className="space-y-3" data-testid="help-workflow">
    <SectionIntro
      title="Universal Lead-to-Completion Workflow"
      subtitle="The 11-step playbook every lead follows from first contact to retention."
      icon={Workflow}
    />
    <ol className="space-y-3">
      {UNIVERSAL_WORKFLOW.map((step) => (
        <li key={step.n} className="rounded-xl border bg-white p-4"
            style={{ borderColor: "#E2E8F0" }} data-testid={`workflow-step-${step.n}`}>
          <div className="flex items-start gap-4">
            <span className="grid place-items-center h-10 w-10 rounded-full font-display text-base font-bold shrink-0"
                  style={{ background: "#0B2545", color: "#FFD774" }}>{step.n}</span>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>{step.title}</h3>
              <div className="mt-2 grid sm:grid-cols-3 gap-3">
                {step.sources?.length > 0 && (
                  <ListBlock label="Sources" items={step.sources} tone="navy" />
                )}
                {step.actions?.length > 0 && (
                  <ListBlock label="Actions" items={step.actions} tone="gold" />
                )}
                {step.statuses?.length > 0 && (
                  <ListBlock label="Status" items={step.statuses} tone="green" />
                )}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

const ListBlock = ({ label, items, tone }) => {
  const palettes = {
    navy: { color: "#0B2545", bg: "rgba(11,37,69,0.06)" },
    gold: { color: "#7A5E12", bg: "rgba(212,175,55,0.16)" },
    green: { color: "#0F6D5D", bg: "rgba(15,109,93,0.10)" },
  };
  const p = palettes[tone] || palettes.navy;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1.5" style={{ color: "#64748B" }}>
        {label}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs px-2 py-1 rounded" style={{ background: p.bg, color: p.color }}>{it}</li>
        ))}
      </ul>
    </div>
  );
};

// =====================================================================
// Exception Workflows
// =====================================================================
const ExceptionsSection = () => (
  <div className="space-y-3" data-testid="help-exceptions">
    <SectionIntro
      title="Exception Workflows"
      subtitle="What to do when the happy path doesn't apply — lost leads, refunds, cancellations."
      icon={AlertTriangle}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {EXCEPTION_WORKFLOWS.map((x) => (
        <div key={x.id} className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}
             data-testid={`exception-${x.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                  style={{ background: "rgba(155,28,28,0.10)", color: "#9B1C1C" }}>
              <AlertTriangle className="h-4 w-4" />
            </span>
            <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>{x.title}</h3>
          </div>
          <div className="space-y-2.5">
            {x.reasons && <ListBlock label="Reasons" items={x.reasons} tone="navy" />}
            {x.actions && <ListBlock label="Actions" items={x.actions} tone="gold" />}
            {x.statuses && <ListBlock label="Status" items={x.statuses} tone="green" />}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// =====================================================================
// Company SOPs
// =====================================================================
const SOPsSection = () => (
  <div className="space-y-3" data-testid="help-sops">
    <SectionIntro
      title="Company SOPs"
      subtitle="Standard operating procedures for every recurring operational area."
      icon={FileText}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {COMPANY_SOPS.map((s) => (
        <details key={s.id} className="group rounded-xl border bg-white p-4 open:shadow-sm"
                 style={{ borderColor: "#E2E8F0" }} data-testid={`sop-${s.id}`}>
          <summary className="flex cursor-pointer items-start gap-3 list-none">
            <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                  style={{ background: "rgba(11,37,69,0.08)", color: "#0B2545" }}>
              <FileText className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>{s.title}</div>
            </div>
            <ChevronRight className="h-4 w-4 mt-1 transition-transform group-open:rotate-90" style={{ color: "#94A3B8" }} />
          </summary>
          <p className="text-xs mt-3 ml-11" style={{ color: "#475569" }}>{s.body}</p>
        </details>
      ))}
    </div>
  </div>
);

// =====================================================================
// Knowledge Base (search)
// =====================================================================
const KnowledgeBaseSection = ({ notes }) => {
  const [q, setQ] = useState("");

  const corpus = useMemo(() => {
    const out = [];
    BUSINESS_GUIDES.forEach((g) => out.push({
      kind: "guide", label: "Guide", color: "#7A5E12", bg: "rgba(212,175,55,0.18)",
      id: g.id, title: g.title, body: g.where + " " + g.steps.join(" "),
    }));
    EXCEPTION_WORKFLOWS.forEach((x) => out.push({
      kind: "exception", label: "Exception", color: "#9B1C1C", bg: "rgba(155,28,28,0.10)",
      id: x.id, title: x.title,
      body: [...(x.reasons || []), ...(x.actions || []), ...(x.statuses || [])].join(" "),
    }));
    COMPANY_SOPS.forEach((s) => out.push({
      kind: "sop", label: "SOP", color: "#0B2545", bg: "rgba(11,37,69,0.08)",
      id: s.id, title: s.title, body: s.body,
    }));
    UNIVERSAL_WORKFLOW.forEach((w) => out.push({
      kind: "workflow", label: "Workflow", color: "#0F6D5D", bg: "rgba(15,109,93,0.10)",
      id: `step-${w.n}`, title: `Step ${w.n}: ${w.title}`,
      body: [...(w.sources || []), ...(w.actions || []), ...(w.statuses || [])].join(" "),
    }));
    (notes || []).forEach((n) => out.push({
      kind: "note", label: "Note", color: "#475569", bg: "#F1F5F9",
      id: n.id, title: n.title, body: (n.body || "") + " " + (CAT_LABEL[n.category] || ""),
    }));
    return out;
  }, [notes]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return corpus.filter((c) =>
      c.title.toLowerCase().includes(s) || (c.body || "").toLowerCase().includes(s)
    ).slice(0, 60);
  }, [q, corpus]);

  return (
    <div className="space-y-3" data-testid="help-kb">
      <SectionIntro
        title="Knowledge Base"
        subtitle="Search every guide, SOP, exception, workflow step, and internal note."
        icon={Search}
      />
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
        <Input
          autoFocus
          placeholder="Search for 'refund', 'contract', 'staffing'…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11"
          data-testid="kb-search"
        />
      </div>
      {q.trim() === "" ? (
        <div className="rounded-2xl border-2 border-dashed py-10 text-center"
             style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
          <Search className="h-7 w-7 mx-auto mb-2" style={{ color: "#94A3B8" }} />
          <p className="text-sm" style={{ color: "#0B2545" }}>
            Start typing to search {corpus.length} articles.
          </p>
        </div>
      ) : results.length === 0 ? (
        <div className="text-sm py-10 text-center" style={{ color: "#64748B" }}>
          No matches for &quot;{q}&quot;.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="kb-results">
          {results.map((r) => (
            <li key={`${r.kind}-${r.id}`} className="rounded-xl border bg-white p-3"
                style={{ borderColor: "#E2E8F0" }}>
              <div className="flex items-start gap-3">
                <Pill bg={r.bg} color={r.color}>{r.label}</Pill>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>{r.title}</div>
                  <div className="text-xs line-clamp-2 mt-0.5" style={{ color: "#64748B" }}>{r.body}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// =====================================================================
// Internal Notes (CRUD)
// =====================================================================
const NoteDialog = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("business_ideas");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setBody(initial?.body || "");
    setCategory(initial?.category || "business_ideas");
  }, [open, initial]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await updateInternalNote(initial.id, { title: title.trim(), body: body || null, category });
        toast.success("Note updated.");
      } else {
        await createInternalNote({ title: title.trim(), body: body || null, category });
        toast.success("Note created.");
      }
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit note" : "New internal note"}</DialogTitle>
          <DialogDescription>Capture ideas, plans, and strategic thinking — private to admins.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="note-title" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="note-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INTERNAL_NOTE_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="note-body"
                      placeholder="Idea, plan, or detail…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="note-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NotesSection = ({ notes, onChanged, loading }) => {
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => (
    filter === "all" ? notes : notes.filter((n) => n.category === filter)
  ), [notes, filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteInternalNote(id);
      toast.success("Deleted.");
      onChanged?.();
    } catch { toast.error("Failed to delete."); }
  };

  return (
    <div className="space-y-4" data-testid="help-notes">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionIntro
          title="Internal Notes & Ideas"
          subtitle="Private brainstorm space — business ideas, expansion plans, partnership notes."
          icon={Lightbulb}
          inline
        />
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
                style={{ background: "#0B2545", color: "#FFFFFF" }}
                data-testid="notes-new">
          <Plus className="h-4 w-4 mr-2" /> New note
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Chip active={filter === "all"} onClick={() => setFilter("all")} label={`All (${notes.length})`} testid="notes-filter-all" />
        {INTERNAL_NOTE_CATEGORIES.map((c) => {
          const cnt = notes.filter((n) => n.category === c.value).length;
          return (
            <Chip key={c.value} active={filter === c.value} onClick={() => setFilter(c.value)}
                  label={`${c.label} (${cnt})`} testid={`notes-filter-${c.value}`} />
          );
        })}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed py-12 text-center"
             style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
          <Lightbulb className="h-7 w-7 mx-auto mb-2" style={{ color: "#94A3B8" }} />
          <p className="font-medium" style={{ color: "#0B2545" }}>No notes yet</p>
          <p className="text-sm mt-1">Capture your next idea — it'll be searchable from the Knowledge Base.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="notes-grid">
          {filtered.map((n) => {
            const tint = CAT_COLORS[n.category] || CAT_COLORS.business_ideas;
            return (
              <div key={n.id} className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}
                   data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <Pill bg={tint.bg} color={tint.color}>{CAT_LABEL[n.category] || n.category}</Pill>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            onClick={() => { setEditing(n); setDialogOpen(true); }}
                            data-testid={`note-edit-${n.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            onClick={() => remove(n.id)} style={{ color: "#9B1C1C" }}
                            data-testid={`note-delete-${n.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="font-display text-sm font-semibold mb-1" style={{ color: "#0B2545" }}>{n.title}</div>
                {n.body && (
                  <p className="text-xs leading-relaxed whitespace-pre-wrap line-clamp-6" style={{ color: "#475569" }}>{n.body}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <NoteDialog open={dialogOpen} initial={editing} onClose={() => setDialogOpen(false)} onSaved={onChanged} />
    </div>
  );
};

const Chip = ({ active, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className="text-xs px-3 py-1.5 rounded-full border transition-colors"
          style={{
            background: active ? "#0B2545" : "#FFFFFF",
            color: active ? "#FFD774" : "#475569",
            borderColor: active ? "#0B2545" : "#E2E8F0",
          }}>
    {label}
  </button>
);

// =====================================================================
// Quick Actions Center — opens dialogs inline (no navigation)
// =====================================================================
const QuickActionsSection = ({ onNotesChanged }) => {
  const [activeKind, setActiveKind] = useState(null);

  const open = (kind) => setActiveKind(kind);
  const close = () => setActiveKind(null);

  return (
    <div className="space-y-3" data-testid="help-entry">
      <SectionIntro
        title="Quick Actions Center"
        subtitle="Click a card to instantly create a record. Anything received by phone, email, referral, networking, walk-in, or partnership can be entered here and flow into the same workflow as website submissions."
        icon={PlusCircle}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {QUICK_ACTIONS.map((card) => {
          const Icon = QUICK_ICON_MAP[card.icon] || PlusCircle;
          return (
            <button key={card.id} onClick={() => open(card.kind)}
                    className="group relative text-left rounded-xl border bg-white p-4 transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "#E2E8F0" }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 12px 32px -16px rgba(11,37,69,0.25)"; e.currentTarget.style.borderColor = "#D4AF37"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
                    data-testid={`entry-${card.id}`}>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-xl shrink-0"
                      style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm font-semibold flex items-center gap-1.5" style={{ color: "#0B2545" }}>
                    <Plus className="h-3.5 w-3.5" style={{ color: "#0F6D5D" }} /> {card.title}
                  </div>
                  <p className="text-xs mt-1 leading-relaxed line-clamp-2" style={{ color: "#64748B" }}>{card.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="rounded-xl border p-4" style={{ background: "rgba(212,175,55,0.06)", borderColor: "rgba(212,175,55,0.4)" }}>
        <p className="text-xs leading-relaxed" style={{ color: "#7A5E12" }}>
          <strong>Goal:</strong> Every lead, client, contract, service request, payment, refund,
          facility, provider, and partnership trackable from first contact through completion —
          inside one centralized platform.
        </p>
      </div>

      <QuickLeadDialog
        open={activeKind === "lead" || activeKind === "client" || activeKind === "service_request"}
        mode={activeKind === "client" ? "client" : activeKind === "service_request" ? "service_request" : "lead"}
        onClose={close} onSaved={close}
      />
      <QuickFacilityDialog open={activeKind === "facility"} onClose={close} onSaved={close} />
      <QuickContractDialog open={activeKind === "contract"} onClose={close} onSaved={close} />
      <QuickInvoiceDialog open={activeKind === "invoice"} onClose={close} onSaved={close} />
      <QuickPaymentDialog open={activeKind === "payment"} onClose={close} onSaved={close} />
      <QuickRefundDialog open={activeKind === "refund"} onClose={close} onSaved={close} />
      <QuickDocumentDialog open={activeKind === "document"} onClose={close} onSaved={close} />
      <QuickStaffCredentialDialog open={activeKind === "staff"} onClose={close} onSaved={close} />
      <QuickTaskDialog open={activeKind === "task"} onClose={close} onSaved={close} />
      <QuickInternalNoteDialog
        open={activeKind === "note"}
        onClose={close}
        onSaved={() => { close(); onNotesChanged?.(); }}
      />
    </div>
  );
};

// =====================================================================
// Shared section heading
// =====================================================================
const SectionIntro = ({ title, subtitle, icon: Icon, inline = false }) => (
  <div className={inline ? "" : "mb-1"}>
    <div className="flex items-center gap-2.5">
      {Icon ? (
        <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
              style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <div>
        <h2 className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>{title}</h2>
        <p className="text-xs mt-0.5 max-w-2xl" style={{ color: "#64748B" }}>{subtitle}</p>
      </div>
    </div>
  </div>
);

// =====================================================================
// Main panel
// =====================================================================
export default function HelpCenterPanel({ onNavigate, onSessionExpired }) {
  const [section, setSection] = useState("guides");
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);

  const refreshNotes = async () => {
    setNotesLoading(true);
    try {
      const data = await listInternalNotes();
      setNotes(data || []);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load notes.");
    } finally { setNotesLoading(false); }
  };

  useEffect(() => { refreshNotes(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="help-center-panel">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
          Operations Manual
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
          Help Center
        </h1>
        <p className="mt-1 text-sm max-w-2xl" style={{ color: "#64748B" }}>
          The official T&amp;J Clinical Solutions operations manual and training
          center — workflows, guides, procedures, and internal knowledge.
        </p>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1.5 border-b pb-2" style={{ borderColor: "#E2E8F0" }} data-testid="help-tabs">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button key={s.id} onClick={() => setSection(s.id)}
                    data-testid={`help-tab-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg transition-colors"
                    style={{
                      background: active ? "rgba(212,175,55,0.18)" : "transparent",
                      color: active ? "#7A5E12" : "#475569",
                      fontWeight: active ? 600 : 500,
                    }}>
              <Icon className="h-4 w-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Active section */}
      {section === "guides" && <GuidesSection />}
      {section === "workflow" && <WorkflowSection />}
      {section === "exceptions" && <ExceptionsSection />}
      {section === "sops" && <SOPsSection />}
      {section === "specimen" && <SpecimenTrainingPanel onSessionExpired={onSessionExpired} />}
      {section === "kb" && <KnowledgeBaseSection notes={notes} />}
      {section === "notes" && <NotesSection notes={notes} loading={notesLoading} onChanged={refreshNotes} />}
      {section === "entry" && <QuickActionsSection onNotesChanged={refreshNotes} />}
    </div>
  );
}
